import { hasNode } from '@common/prosemirror/model/node-find';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class DropDownHeadNodeView extends NodeViewBase implements NodeView {
  private buttonDOM: HTMLButtonElement;
  private iconDOM: HTMLSpanElement;
  private onButtonClicked: (event: MouseEvent) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-dropdownhead-content'));
    // Do not insert the button into the DOM yet. That will be done in syncNode based on whether there is a hotspot node.
    this.buttonDOM = document.createElement('button');
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-dropdownhead-button');
    this.iconDOM = this.buttonDOM.appendChild(document.createElement('span'));
    this.iconDOM.setAttribute('draggable', 'false');
    this.iconDOM.classList.add('icon-down-arrow-90', 'mc-expansion-button-indicator');

    // Listen for button click events to fire the toggle event
    this.onButtonClicked = () => {
      // Dispatch a custom event to notify the drop down node view of the toggle
      this.dom.dispatchEvent(new CustomEvent('toggle-drop-down', {
        bubbles: true,
        composed: true,
        cancelable: false
      }));
    };

    this.buttonDOM.addEventListener('click', this.onButtonClicked);

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.buttonDOM.removeEventListener('click', this.onButtonClicked);
    super.destroy();
  }

  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    const isOpen = this.dom.parentElement?.hasAttribute('mc-lwe-drop-down-open');
    const $pos = this.editorView.state.doc.resolve(pos);

    // The head's button is always on the right (appended as last child)
    if (dir < 0) {
      // Navigate to the end of the head content
      return pos + this.node.nodeSize - 1;
    }

    // If the drop down body is open, navigate to the start of the drop down body
    if (isOpen) {
      return pos + this.node.nodeSize;
    }

    // Body is closed - navigate past the entire drop down container
    return $pos.end($pos.depth) + 1;
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }

  syncNode(node: ProseMirrorNode) {
    // If the drop down head does not have a hotspot descendant, then the head needs to display a trigger button to open the drop down
    const hasHotspotNode = hasNode(node, child => child.type.name === 'madcapdropdownhotspot');

    if (hasHotspotNode) {
      this.buttonDOM.remove();
    } else {
      // Ensure the button is the last child
      if (this.buttonDOM !== this.dom.lastChild) {
        this.dom.appendChild(this.buttonDOM);
      }
    }
  }
}
